/**
 * Browser-Benachrichtigungen: Service Worker registrieren, Abo an- und
 * abmelden.
 *
 * Die Erlaubnis liegt beim Browser, das Abo beim Backend. Beides muss
 * zusammenpassen - deshalb liest `pushStatus()` immer beide Seiten und nicht
 * nur die eine. Ein Nutzer, der die Erlaubnis in den Browser-Einstellungen
 * entzieht, soll in der Oberfläche nicht weiter als angemeldet erscheinen.
 */

import apiClient from './apiClient';

const SW_PFAD = '/sw.js';

export interface PushStatus {
  /** Browser kann überhaupt Push (Service Worker + PushManager vorhanden) */
  unterstuetzt: boolean;
  /** Server hat VAPID-Schlüssel konfiguriert */
  konfiguriert: boolean;
  /** 'granted' | 'denied' | 'default' - Stand der Browser-Erlaubnis */
  erlaubnis: NotificationPermission | 'unavailable';
  /** Dieses Gerät ist beim Backend registriert */
  abonniert: boolean;
  /** Ist ein Service Worker für diese Seite registriert? */
  serviceWorker: boolean;
  /** Basis-URL, gegen die das Frontend spricht - verrät einen falschen Server */
  apiBasis: string;
  /** Klartext, warum es nicht geht. Leer, wenn alles in Ordnung ist. */
  grund: string;
}

/** Base64url aus dem VAPID-Schlüssel in das von der Push-API erwartete Format. */
function base64UrlZuUint8Array(base64: string): Uint8Array {
  const auffuellen = '='.repeat((4 - (base64.length % 4)) % 4);
  const normal = (base64 + auffuellen).replace(/-/g, '+').replace(/_/g, '/');
  const roh = window.atob(normal);
  const bytes = new Uint8Array(roh.length);
  for (let i = 0; i < roh.length; i += 1) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

export function pushUnterstuetzt(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Service Worker registrieren und auf Betriebsbereitschaft warten.
 *
 * `navigator.serviceWorker.ready` ist entscheidend: direkt nach `register()`
 * ist der Worker noch nicht aktiv, und ein `subscribe()` darauf schlägt fehl.
 */
export async function serviceWorkerRegistrieren(): Promise<ServiceWorkerRegistration | null> {
  if (!pushUnterstuetzt()) return null;
  try {
    await navigator.serviceWorker.register(SW_PFAD);
    return await navigator.serviceWorker.ready;
  } catch (err) {
    console.error('[Push] Service Worker konnte nicht registriert werden:', err);
    return null;
  }
}

/** Basis-URL, gegen die der apiClient spricht. */
export function apiBasis(): string {
  return localStorage.getItem('club_url') || process.env.REACT_APP_API_URL || 'http://localhost:4000';
}

/**
 * Aktuellen Stand aus Browser und Backend zusammenführen.
 *
 * Liefert bei jedem Hindernis einen `grund` im Klartext. Das ist kein
 * Entwickler-Luxus: Ohne ihn sieht ein Mitglied nur einen Knopf, der nichts
 * tut, und der Vorstand bekommt eine Mail "geht nicht" ohne jede Spur.
 */
export async function pushStatus(): Promise<PushStatus> {
  const basis = apiBasis();

  if (!pushUnterstuetzt()) {
    const fehlt = [
      !('serviceWorker' in navigator) && 'Service Worker',
      !('PushManager' in window) && 'PushManager',
      !('Notification' in window) && 'Notification-API',
    ].filter(Boolean).join(', ');
    return {
      unterstuetzt: false, konfiguriert: false, erlaubnis: 'unavailable', abonniert: false,
      serviceWorker: false, apiBasis: basis,
      grund: `Dieser Browser kann kein Web Push (fehlt: ${fehlt}).`,
    };
  }

  const erlaubnis = Notification.permission;
  let endpoint: string | undefined;
  let serviceWorker = false;

  try {
    const registrierung = await navigator.serviceWorker.getRegistration(SW_PFAD);
    serviceWorker = Boolean(registrierung);
    const abo = await registrierung?.pushManager.getSubscription();
    endpoint = abo?.endpoint;
  } catch {
    // Kein Abo im Browser - der Server entscheidet dann über 'abonniert'.
  }

  try {
    const antwort = await apiClient.get('/api/push/status', { params: endpoint ? { endpoint } : {} });
    const konfiguriert = Boolean(antwort.data?.configured);
    const abonniert = Boolean(antwort.data?.subscribed) && Boolean(endpoint) && erlaubnis === 'granted';

    let grund = '';
    if (!konfiguriert) grund = `Der Server unter ${basis} hat keine Push-Schlüssel hinterlegt (VAPID).`;
    else if (erlaubnis === 'denied') grund = 'Der Browser blockiert Benachrichtigungen für diese Seite.';
    else if (!serviceWorker) grund = 'Es ist kein Service Worker registriert. Seite einmal neu laden (Cmd+Shift+R).';

    return { unterstuetzt: true, konfiguriert, erlaubnis, abonniert, serviceWorker, apiBasis: basis, grund };
  } catch (err: any) {
    // Entscheidend für die Fehlersuche: WELCHER Server hat WIE geantwortet.
    const code = err?.response?.status;
    return {
      unterstuetzt: true, konfiguriert: false, erlaubnis, abonniert: false, serviceWorker,
      apiBasis: basis,
      grund: code
        ? `Der Server unter ${basis} antwortet auf /api/push/status mit HTTP ${code}.`
        : `Der Server unter ${basis} ist nicht erreichbar.`,
    };
  }
}

/**
 * Benachrichtigungen einschalten.
 *
 * Wirft mit einer für die Oberfläche brauchbaren Meldung, statt stillschweigend
 * false zurückzugeben - der Nutzer soll erfahren, WARUM es nicht ging.
 */
export async function pushAktivieren(): Promise<void> {
  if (!pushUnterstuetzt()) {
    throw new Error('Dieser Browser unterstützt keine Benachrichtigungen.');
  }

  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis === 'denied') {
    throw new Error('Benachrichtigungen sind für diese Seite blockiert. Das lässt sich nur in den Browser-Einstellungen wieder ändern.');
  }
  if (erlaubnis !== 'granted') {
    throw new Error('Benachrichtigungen wurden nicht erlaubt.');
  }

  const schluessel = await apiClient.get('/api/push/public-key');
  const publicKey: string | undefined = schluessel.data?.publicKey;
  if (!publicKey) throw new Error('Der Server hat keinen Push-Schlüssel hinterlegt.');

  const registrierung = await serviceWorkerRegistrieren();
  if (!registrierung) throw new Error('Der Service Worker konnte nicht gestartet werden.');

  // Ein bestehendes Abo kann auf einem ALTEN Schlüssel beruhen (z. B. nachdem
  // der Server neue VAPID-Schlüssel bekommen hat). Dann ist es wertlos und wird
  // ersetzt, statt zurückgegeben zu werden.
  const vorhanden = await registrierung.pushManager.getSubscription();
  if (vorhanden) await vorhanden.unsubscribe();

  const abo = await registrierung.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlZuUint8Array(publicKey) as BufferSource,
  });

  const json = abo.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await apiClient.post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
}

/** Benachrichtigungen für dieses Gerät abschalten. */
export async function pushDeaktivieren(): Promise<void> {
  const registrierung = await navigator.serviceWorker.getRegistration(SW_PFAD);
  const abo = await registrierung?.pushManager.getSubscription();
  if (!abo) return;

  // Erst beim Backend abmelden, dann im Browser kündigen. Andersherum wäre der
  // endpoint schon weg und die Zeile in der Datenbank bliebe als Karteileiche
  // stehen.
  try {
    await apiClient.delete('/api/push/subscribe', { data: { endpoint: abo.endpoint } });
  } finally {
    await abo.unsubscribe();
  }
}

/** Probenachricht an die eigenen Geräte auslösen. */
export async function pushTesten(): Promise<number> {
  const antwort = await apiClient.post('/api/push/test');
  return Number(antwort.data?.zugestellt ?? 0);
}
